import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import { createFansGroup } from 'services/endpoints/fanGroup'
import './_add-fans-group-modal.scss'

interface AddFansGroupModalProps {
    dontClearModal?: boolean
    customCloseFn?: () => void
    groupNames?: string[]
    onCreated?: (group: any) => void
}

// Match backend: trim + collapse whitespace into single spaces
const normalizeGroupName = (raw: string) =>
    raw.replace(/\s+/g, ' ').trim()

// Match backend acceptable chars: letters + spaces only
const ACCEPTABLE_NAME_REGEX = /^[A-Za-z ]+$/

// Keep this consistent with backend. Your UI currently uses 30.
const MAX_LEN = 30

export const AddFansGroupModal = ({
    dontClearModal,
    customCloseFn,
    groupNames,
    onCreated
}: AddFansGroupModalProps) => {
    const [fansGroupName, setFansGroupName] = useState('')
    const modalData = useModalContext()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    // For validation we use normalized version (collapse whitespace)
    const normalizedName = useMemo(
        () => normalizeGroupName(fansGroupName),
        [fansGroupName]
    )

    const validation = useMemo(() => {
        // Empty
        if (normalizedName.length === 0) {
            return {
                isValid: false,
                error:
                    // keep your existing localized toast key; this is inline though
                    t('error:pleaseInputGroupName')
            }
        }

        // Length
        if (normalizedName.length > MAX_LEN) {
            return {
                isValid: false,
                error: `${t('error:tooManyCharacters') ?? 'Too many characters'} (${normalizedName.length}/${MAX_LEN})`
            }
        }

        // Character set
        if (!ACCEPTABLE_NAME_REGEX.test(normalizedName)) {
            return {
                isValid: false,
                error:
                    // You can localize this if you want; leaving plain text is fine too.
                    t('error:invalidCharacters') ||
                    'Only letters (A–Z) and spaces are allowed.'
            }
        }

        // Reserved names + duplicates (your existing rules)
        const lower = normalizedName.toLowerCase()

        if (lower === 'friends' || lower === 'fans' || lower === 'all') {
            return {
                isValid: false,
                error: t('error:invalidGroupName')
            }
        }

        if (groupNames?.length) {
            const groupNamesLowerCase = groupNames.map(g =>
                g.toLowerCase()
            )

            // You had underscore normalization here; keep it, but apply to normalized name
            const normalizedForCompare = lower.replace('_', ' ').trim()

            if (
                groupNamesLowerCase.includes(normalizedForCompare) ||
                normalizedForCompare === 'all active'
            ) {
                return {
                    isValid: false,
                    error: t('error:groupWithThatNameAlreadyExists')
                }
            }
        }

        return { isValid: true, error: '' }
    }, [normalizedName, groupNames, t])

    const addFansGroup = useMutation(
        async () => {
            // Send normalized name to backend (matches backend normalization intent)
            return await createFansGroup(normalizedName)
        },
        {
        onSuccess: (response: any) => {
            const createdGroup =
                response?.data?.message?.group

            console.log('[GROUP CREATE]', {
                response,
                createdGroup
            })

            if (createdGroup) {
                onCreated?.(createdGroup)
            }

            queryClient.invalidateQueries('fans-groups')

            if (!dontClearModal) {
                modalData.clearModal()
            }

            customCloseFn?.()
        },
            onError: () => {
                addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
                queryClient.invalidateQueries('fans-groups')
                if (!dontClearModal) modalData.clearModal()
                if (customCloseFn) customCloseFn()
            }
        }
    )

    const addFansGroupSubmit = () => {
        // Block submission if invalid (button is also disabled)
        if (!validation.isValid) {
            // Optional: you can remove this toast if you want only inline error
            addToast('error', validation.error)
            return
        }
        addFansGroup.mutate()
    }

    const helperText = validation.isValid
        ? `${normalizedName.length} / ${MAX_LEN} ${t('characters')}`
        : validation.error

    return (
        <div className='add-fans-group-modal-container'>
            <InputCard
                value={fansGroupName}
                changeFn={(val: any) => {
                    setFansGroupName(val)
                }}
                label={t('groupName')}
                hasDesc={true}
                // Show error where character count was
                desc={helperText}
                maxChars={MAX_LEN}
                type='text'
                // If your InputCard supports it, pass an error flag/class.
                // If it doesn't, the desc styling below handles the red text requirement.
            />

            {/* If InputCard can't style desc red, this wrapper ensures it */}
            {!validation.isValid && (
                <div className='add-fans-group-modal-error'>
                    {/* This is optional; remove if you ONLY want it in desc */}
                </div>
            )}

            <div className='add-fans-group-modal-buttons'>
                <Button
                    text={t('back')}
                    color='transparent'
                    type='transparent--black1px'
                    clickFn={() => {
                        if (!dontClearModal) modalData.clearModal()
                        if (customCloseFn) customCloseFn()
                        setFansGroupName('')
                    }}
                    height='3'
                    width='13'
                    font='mont-14-normal'
                />

                <Button
                    text={t('add')}
                    color='blue'
                    customClass={`add-fans-group-modal-button ${
                        addFansGroup.isLoading
                            ? 'add-fans-group-modal-button-loading'
                            : ''
                    }`}
                    clickFn={addFansGroupSubmit}
                    // Many of your Button components accept "disabled".
                    // If yours doesn't, see note below for a safe fallback.
                    disabled={!validation.isValid || addFansGroup.isLoading}
                    height='3'
                    width='13'
                    font='mont-14-normal'
                />
            </div>
        </div>
    )
}
